import {
  getFirestore,
  collection,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore"
import { Invitation, MemberPermission } from "@/types/schema"
import { addUserToPortfolio } from "@/utils/firestore/users/portfolioMembership"

/**
 * Get invitation by token
 */
export const getInvitationByToken = async (
  token: string
): Promise<Invitation | null> => {
  try {
    const db = getFirestore()
    const invitationsRef = collection(db, "invitations")
    const invitationDoc = doc(invitationsRef, token)
    const invitationSnapshot = await getDoc(invitationDoc)

    if (invitationSnapshot.exists()) {
      return {
        ...(invitationSnapshot.data() as Omit<Invitation, "id">),
        id: invitationSnapshot.id,
      } as Invitation
    }

    return null
  } catch (error) {
    console.error("Error getting invitation:", error)
    return null
  }
}

/**
 * Accept invitation
 */
export const acceptInvitation = async (
  invitationId: string,
  userId: string
): Promise<boolean> => {
  try {
    const db = getFirestore()
    const invitationRef = doc(db, "invitations", invitationId)
    const invitationSnapshot = await getDoc(invitationRef)

    if (!invitationSnapshot.exists()) {
      console.error("Invitation not found:", invitationId)
      return false
    }

    const invitation = invitationSnapshot.data() as Invitation

    // Update invitation status
    await updateDoc(invitationRef, {
      status: "accepted",
      acceptedAt: new Date(),
      userId,
    })

    // Add user to portfolio if this is a portfolio invitation
    if (invitation.entityType === "portfolio" && invitation.entityId) {
      const permissions = (invitation.permissions as MemberPermission[]) || []
      const role = invitation.role || "Manager"

      await addUserToPortfolio(
        userId,
        invitation.entityId,
        role,
        permissions,
        invitation.firstName,
        invitation.lastName,
        invitation.email,
        invitation.phone
      )

      console.log(
        `User ${userId} added to portfolio ${invitation.entityId} from invitation`
      )
    }

    return true
  } catch (error) {
    console.error("Error accepting invitation:", error)
    return false
  }
}

/**
 * Decline invitation
 */
export const declineInvitation = async (
  invitationId: string
): Promise<boolean> => {
  try {
    const db = getFirestore()
    const invitationRef = doc(db, "invitations", invitationId)

    await updateDoc(invitationRef, {
      status: "declined",
      declinedAt: new Date(),
    })

    return true
  } catch (error) {
    console.error("Error declining invitation:", error)
    return false
  }
}
